import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiAlertTriangle } from 'react-icons/fi';
import { Button } from '../ui/button';
import PricingPlans from '../billing/PricingPlans';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { useWorkspace } from '../../context/WorkspaceContext';
import WorkspaceSelector from '../layout/WorkspaceSelector';

const TrialExpiredModal = () => {
  const navigate = useNavigate();
  const { signOut } = useSupabaseAuth();
  const { userRole, toggleAdminOverride, adminOverrideEnabled } = useWorkspace();

  const handlePlanSelect = (priceId) => {
    // Navigate to checkout logic
    navigate(`/dashboard?checkout_plan=${priceId}`);
    // Since this modal is blocking, we might want to let the navigation happen
    // but the modal will persist until the subscription is active.
    // However, the checkout usually involves a redirect to Stripe.
    // If PricingPlans handles the logic to redirect, we are good.
    // PricingPlans component calls onSelectPlan(stripe_price_id).
    // The implementation in LandingPage uses navigate(`/dashboard?checkout_plan=${priceId}`).
    // The Dashboard likely handles the checkout init on mount if that param is present.
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-charcoal-900/90 backdrop-blur-sm min-h-0">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="max-h-[90vh] flex flex-col bg-white dark:bg-charcoal-900 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden border border-charcoal-200 dark:border-charcoal-800 my-8 relative">

          {/* Header */}
        <div className="shrink-0 bg-red-50 dark:bg-red-900/20 p-8 text-center border-b border-red-100 dark:border-red-800/30">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiLock size={32} />
          </div>
          <h2 className="text-3xl font-bold text-charcoal-900 dark:text-white mb-2">
            Your 7-day trial has ended.
          </h2>
          <p className="text-lg text-charcoal-600 dark:text-charcoal-300 max-w-2xl mx-auto">
            Upgrade now to continue generating comprehensive marketing strategies across unlimited workspaces.
          </p>
        </div>

        {/* Pricing Section */}
        <div className="flex-1 p-8 bg-charcoal-50 dark:bg-charcoal-950 overflow-y-auto">
          <PricingPlans onSelectPlan={handlePlanSelect} isDashboard={true} />
        </div>

        {/* Footer */}
        <div className="shrink-0 p-6 bg-white dark:bg-charcoal-900 border-t border-charcoal-200 dark:border-charcoal-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
                onClick={handleLogout}
                className="text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200 underline order-3 sm:order-1"
            >
                Log out and return to home
            </button>

            <div className="order-2 sm:order-2 flex-1 flex justify-center">
                <WorkspaceSelector showAppLogo={false} compactMode={true} />
            </div>

            {userRole === 'super_admin' && (
                <button
                    onClick={toggleAdminOverride}
                    className="text-sm font-medium px-4 py-2 bg-charcoal-100 hover:bg-charcoal-200 dark:bg-charcoal-800 dark:hover:bg-charcoal-700 text-charcoal-900 dark:text-charcoal-100 rounded-md transition-colors order-1 sm:order-3 flex items-center gap-2"
                >
                    <FiLock size={14} /> Restore Super Admin Access
                </button>
            )}
        </div>

      </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
